import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../lib/Seo";
import RevealText from "../lib/RevealText";
import Reveal from "../lib/Reveal";
import { ArrowUpRight, Check, Plane, Camera, Wine, Users, Compass, Route, Binoculars } from "lucide-react";

const img = (id, w = 2400) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;

const experiences = [
  {
    slug: "weekend-wild-masai-mara",
    label: "01",
    name: "Weekend Wild — Masai Mara",
    country: "Kenya",
    duration: "3 nights · Fly-in Friday, out Monday",
    tagline: "The Mara at close range — a long weekend, no compromise.",
    hero: img("1552410260-0fd9b577afa6"),
    for: "For guests with two working days and one big appetite for the wild.",
    inclusions: [
      "Return charter flight from Nairobi",
      "Three nights at a premium tented camp",
      "Private 4x4 with senior Njia guide",
      "All meals & premium drinks",
      "Sundowners on the plains",
      "Airport transfers",
    ],
    signature: "Optional dawn balloon safari — champagne breakfast on the plains.",
    lodge: "Mara Plains Camp",
  },
  {
    slug: "balloon-and-bush-serengeti",
    label: "02",
    name: "Balloon & Bush — Serengeti",
    country: "Tanzania",
    duration: "4 nights · Long weekend",
    tagline: "The signature Serengeti moment — captured in four nights.",
    hero: img("1516426122078-c23e76319801"),
    for: "For couples wanting the postcard, done well.",
    inclusions: [
      "Domestic flights Arusha ⇄ Serengeti",
      "Four nights at Nomad Serengeti Safari Camp",
      "Dawn hot air balloon safari with champagne breakfast",
      "Private 4x4 with senior guide",
      "All meals & premium drinks",
      "Bush dinner under the stars",
    ],
    signature: "One private kopje evening — dinner set up just for two.",
    lodge: "Nomad Serengeti Safari Camp",
  },
  {
    slug: "bwindi-72-gorillas",
    label: "03",
    name: "Bwindi 72 — Gorillas in Three Days",
    country: "Uganda",
    duration: "3 nights · Focused primate trek",
    tagline: "One hour with the mountain gorillas — the whole trip built around it.",
    hero: "https://upload.wikimedia.org/wikipedia/commons/a/a0/Susa_group%2C_mountain_gorilla.jpg",
    for: "For photographers and adventure-first travellers with limited leave.",
    inclusions: [
      "Return charter Entebbe ⇄ Bwindi",
      "Three nights at Clouds Mountain Gorilla Lodge",
      "One gorilla trek permit & senior tracker",
      "Batwa cultural exchange (respectful, non-staged)",
      "All meals & premium drinks",
      "Airport transfers",
    ],
    signature: "Add a second trek permit if you wish — some guests choose to.",
    lodge: "Clouds Mountain Gorilla Lodge",
  },
  {
    slug: "zanzibar-bush-and-beach",
    label: "04",
    name: "Zanzibar Bush & Beach",
    country: "Tanzania",
    duration: "5 nights · Crater + coast",
    tagline: "Two of Africa's icons in one, tight, five-night edit.",
    hero: img("1519046904884-53103b34b206"),
    for: "For guests who want one bush moment and one beach afternoon — nothing wasted.",
    inclusions: [
      "Two nights at Entamanu Ngorongoro (crater rim)",
      "Full-day Ngorongoro Crater game drive",
      "Three nights at The Residence Zanzibar",
      "Private transfers throughout",
      "Domestic flights Arusha → Zanzibar",
      "All meals & premium drinks",
    ],
    signature: "Sunset dhow cruise on the last evening.",
    lodge: "Entamanu Ngorongoro & The Residence Zanzibar",
  },
];

export default function Experiences() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  return (
    <>
      <Seo
        title="Experiences | Njia Safaris — Curated Safari Packages for Young Professionals"
        description="Njia Safaris' curated Experiences — short-form, all-inclusive safari packages designed for young professionals. Weekend Mara, gorilla trek in 72 hours, Serengeti balloon."
        path="/experiences"
      />
      <Nav />

      {/* HERO */}
      <section
        ref={heroRef}
        className="relative h-[100svh] overflow-hidden section-dark"
        data-testid="experiences-hero"
      >
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img
            src={img("1547471080-7cc2caa01a7e")}
            alt="Kenya sunrise"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/35 to-black/80" />
        <div className="grain-overlay" />

        <div className="relative z-10 h-full container-x flex flex-col justify-end pb-16 md:pb-24">
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-16 bg-[color:var(--gold)]" />
            <span className="eyebrow eyebrow-invert">Experiences · Curated</span>
          </div>
          <RevealText
            as="h1"
            className="h-display text-[12vw] md:text-[8.5vw] text-[color:var(--ivory)] leading-[0.95]"
            lines={["Short leave.", <em key="e" className="italic text-[color:var(--gold-3)]">Long memory.</em>]}
            stagger={110}
          />
          <p className="mt-8 font-italic-display text-2xl md:text-3xl text-[color:var(--sand)] max-w-3xl">
            Four curated packages for young professionals — all-inclusive, three to five nights, nothing to plan.
          </p>
        </div>
      </section>

      {/* PROMISE STRIP */}
      <section className="section-forest py-12" data-testid="experiences-promise">
        <div className="container-x grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
          {[
            { icon: Plane, label: "Flights & transfers", detail: "All domestic legs included" },
            { icon: Users, label: "Private guide", detail: "One senior guide throughout" },
            { icon: Camera, label: "Signature moments", detail: "Balloons, sundowners, treks" },
            { icon: Wine, label: "All meals & drinks", detail: "Premium bar included" },
          ].map((p) => (
            <div key={p.label} className="flex items-start gap-3">
              <p.icon size={20} strokeWidth={1.4} className="text-[color:var(--gold-3)] mt-1 shrink-0" />
              <div>
                <p className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-3)] mb-1">
                  {p.label}
                </p>
                <p className="text-[color:var(--ivory)] text-sm leading-snug">{p.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* INTRO */}
      <section className="section-y container-x" data-testid="experiences-intro">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="eyebrow mb-4">Designed for</p>
            <RevealText
              as="h2"
              className="h-display text-4xl md:text-5xl"
              lines={["Young", <em key="e" className="italic text-[color:var(--forest)]">professionals.</em>]}
            />
          </div>
          <div className="md:col-span-6 md:col-start-6">
            <p className="text-lg md:text-xl leading-relaxed max-w-xl">
              These are not the twelve-day grand tours. They are our tightest, most decisive short-form journeys —
              designed for guests with limited leave, a serious camera, and a taste for the well-composed.
            </p>
            <p className="mt-6 text-lg leading-relaxed text-[color:var(--ink)]/80 max-w-xl">
              Everything included. Nothing to plan. Book one email and we handle the rest.
            </p>
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="pb-24 container-x" data-testid="experiences-list">
        <div className="space-y-24 md:space-y-32">
          {experiences.map((exp, i) => {
            const flip = i % 2 === 1;
            return (
              <article
                key={exp.slug}
                className="grid md:grid-cols-12 gap-10 md:gap-16 items-center"
                data-testid={`experience-${exp.slug}`}
              >
                {/* Image */}
                <Reveal
                  className={`md:col-span-6 ${flip ? "md:order-2" : ""}`}
                  delay={0.05}
                >
                  <div className="img-frame aspect-[4/5] shadow-editorial">
                    <img src={exp.hero} alt={exp.name} loading="lazy" />
                  </div>
                </Reveal>

                {/* Content */}
                <div className={`md:col-span-6 ${flip ? "md:order-1" : ""}`}>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="chapter-num" style={{ fontSize: "3.5rem" }}>
                      {exp.label}
                    </span>
                    <div>
                      <p className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--forest)]">
                        {exp.country}
                      </p>
                      <p className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--ink)]/60 mt-1">
                        {exp.duration}
                      </p>
                    </div>
                  </div>

                  <h3 className="font-display text-4xl md:text-5xl leading-[0.95] mb-4">
                    {exp.name}
                  </h3>
                  <p className="font-italic-display text-xl md:text-2xl text-[color:var(--forest)] leading-snug mb-6">
                    {exp.tagline}
                  </p>
                  <p className="text-[15px] leading-relaxed text-[color:var(--ink)]/80 max-w-md mb-8">
                    {exp.for}
                  </p>

                  <div className="hairline mb-6" />

                  {/* Inclusions */}
                  <p className="eyebrow mb-4">What&rsquo;s included</p>
                  <ul className="space-y-2 mb-6 max-w-md">
                    {exp.inclusions.map((inc) => (
                      <li key={inc} className="flex items-start gap-3 text-[14px]">
                        <Check
                          size={14}
                          strokeWidth={1.8}
                          className="text-[color:var(--gold-2)] mt-1 shrink-0"
                        />
                        <span>{inc}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Signature line */}
                  <p className="font-italic-display text-lg text-[color:var(--forest)] max-w-md mb-8">
                    <span className="mono not-italic text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-2)] mr-2">
                      Signature
                    </span>
                    {exp.signature}
                  </p>

                  <div className="flex flex-wrap items-center gap-4">
                    <Link
                      to="/contact"
                      state={{ journey: exp.name }}
                      className="btn-gold"
                      data-testid={`book-now-${exp.slug}`}
                    >
                      Book Now
                      <ArrowUpRight size={14} strokeWidth={1.6} />
                    </Link>
                    <p className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--ink)]/60">
                      Lodge · {exp.lodge}
                    </p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* WHY CHOOSE OUR SAFARIS */}
      <section className="section-sand section-y" data-testid="why-choose">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-10 mb-16 md:mb-20">
            <div className="md:col-span-4">
              <p className="eyebrow mb-4">The Njia difference</p>
              <RevealText
                as="h2"
                className="h-display text-5xl md:text-6xl leading-[0.95]"
                lines={["Why choose", <em key="e" className="italic text-[color:var(--forest)]">our safaris.</em>]}
              />
            </div>
            <div className="md:col-span-6 md:col-start-7 self-end">
              <p className="font-italic-display text-xl md:text-2xl text-[color:var(--forest)] leading-snug max-w-xl">
                Three principles, applied to every journey — the reason our guests return, and refer.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-10 md:gap-16">
            {[
              {
                num: "01",
                Icon: Compass,
                title: "Expert Curation",
                lede: "Hand-picked camps",
                body:
                  "Every lodge on our list has been visited, tested and trusted. We turn down more than we accept — the standard is set by us, not by the season.",
              },
              {
                num: "02",
                Icon: Route,
                title: "Seamless Logistics",
                lede: "We handle everything",
                body:
                  "Charter flights, private transfers, park permits, permits again, dietary preferences, camera batteries at the airstrip. You arrive with a suitcase. We handle the rest.",
              },
              {
                num: "03",
                Icon: Binoculars,
                title: "Specialist Knowledge",
                lede: "For travellers and photographers",
                body:
                  "Whether it is your first safari or your fifteenth, your guide is briefed to match. First-timers get context; photographers and filmmakers get light, angle, and patience.",
              },
            ].map((f, i) => (
              <Reveal key={f.num} delay={i * 0.08}>
                <div className="border-t border-[color:var(--ink)] pt-6 h-full">
                  <div className="flex items-baseline gap-4 mb-6">
                    <span className="chapter-num" style={{ fontSize: "3rem" }}>
                      {f.num}
                    </span>
                    <f.Icon size={22} strokeWidth={1.4} className="text-[color:var(--forest)]" />
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl leading-tight mb-2">
                    {f.title}
                  </h3>
                  <p className="font-italic-display text-lg text-[color:var(--forest)] mb-6">
                    {f.lede}
                  </p>
                  <p className="text-[15px] leading-relaxed text-[color:var(--ink)]/80 max-w-sm">
                    {f.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="section-dark py-24 md:py-32" data-testid="experiences-cta">
        <div className="container-x grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="eyebrow eyebrow-invert mb-6">Something else in mind?</p>
            <RevealText
              as="h2"
              className="h-display text-5xl md:text-6xl text-[color:var(--ivory)]"
              lines={["We compose", <em key="e" className="italic text-[color:var(--gold-3)]">private editions too.</em>]}
            />
          </div>
          <div className="md:col-span-5">
            <p className="text-lg leading-relaxed text-[color:var(--sand)] max-w-md mb-8">
              These four are our most-requested short-form experiences. If your dates, group or interests
              don&rsquo;t quite fit — tell us. We compose privately for guests who ask.
            </p>
            <Link to="/contact" className="btn-ghost btn-ghost-invert" data-testid="experiences-custom-cta">
              Compose a private edition
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
